import React from "react";
import { Box, alpha, useTheme } from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { PtiAssetType, PtiItemType } from "../../../api/ptiApi";
import {
    getPtiZoneIconSize,
    isPtiIconBodyZone,
    PtiZoneIcon,
} from "../ptiZoneIcons";

interface PtiMiniDiagramProps {
    assetType: PtiAssetType;
    highlightPositionKey: string;
    highlightItemType: PtiItemType;
    width?: number;
    onZonePress?: (positionKey: string, itemType: PtiItemType) => void;
    /** Zones that already have a captured photo (shown in green, like mobile). */
    photoZones?: Array<{ positionKey: string; itemType: PtiItemType }>;
}

const V_PATH =
    "M 78,10 Q 78,4 88,4 L 152,4 Q 162,4 162,10 L 162,58 L 186,66 Q 192,68 192,74 L 192,332 Q 192,342 182,342 L 58,342 Q 48,342 48,332 L 48,74 Q 48,68 54,66 L 78,58 Z";

const V_BODY: Array<{ key: string; x: number; y: number; w: number; h: number }> = [
    { key: "front", x: 72, y: 4, w: 96, h: 34 },
    { key: "rear", x: 52, y: 308, w: 136, h: 34 },
    // Stop above drive tires so tire hit-targets stay on top of the sides
    { key: "driver_side", x: 48, y: 78, w: 28, h: 168 },
    { key: "passenger_side", x: 164, y: 78, w: 28, h: 168 },
    { key: "dashboard", x: 86, y: 48, w: 68, h: 36 },
    { key: "coolant", x: 76, y: 92, w: 40, h: 24 },
    { key: "oil", x: 124, y: 92, w: 40, h: 24 },
    { key: "fifth_wheel", x: 90, y: 250, w: 60, h: 36 },
];

const V_TIRES: Array<{ key: string; cx: number; cy: number }> = [
    { key: "steer_left", cx: 32, cy: 42 },
    { key: "steer_right", cx: 208, cy: 42 },
    { key: "drive_1_left_outer", cx: 26, cy: 262 },
    { key: "drive_1_left_inner", cx: 44, cy: 262 },
    { key: "drive_1_right_inner", cx: 196, cy: 262 },
    { key: "drive_1_right_outer", cx: 214, cy: 262 },
    { key: "drive_2_left_outer", cx: 26, cy: 298 },
    { key: "drive_2_left_inner", cx: 44, cy: 298 },
    { key: "drive_2_right_inner", cx: 196, cy: 298 },
    { key: "drive_2_right_outer", cx: 214, cy: 298 },
    // Legacy keys for historical PTIs
    { key: "drive_1_left", cx: 35, cy: 262 },
    { key: "drive_1_right", cx: 205, cy: 262 },
    { key: "drive_2_left", cx: 35, cy: 298 },
    { key: "drive_2_right", cx: 205, cy: 298 },
];

const T_BODY: Array<{ key: string; x: number; y: number; w: number; h: number }> = [
    { key: "front", x: 52, y: 4, w: 136, h: 30 },
    { key: "rear", x: 52, y: 386, w: 136, h: 30 },
    // Stop above trailer tires; keep sides inset so duals sit fully outside the body
    { key: "driver_side", x: 52, y: 36, w: 22, h: 300 },
    { key: "passenger_side", x: 166, y: 36, w: 22, h: 300 },
    { key: "left_inside", x: 78, y: 60, w: 40, h: 120 },
    { key: "right_inside", x: 122, y: 60, w: 40, h: 120 },
    { key: "inside_floor", x: 78, y: 190, w: 84, h: 56 },
    { key: "doors", x: 78, y: 340, w: 84, h: 36 },
];

const T_TIRES: Array<{ key: string; cx: number; cy: number }> = [
    { key: "axle_1_left_outer", cx: 16, cy: 358 },
    { key: "axle_1_left_inner", cx: 34, cy: 358 },
    { key: "axle_1_right_inner", cx: 206, cy: 358 },
    { key: "axle_1_right_outer", cx: 224, cy: 358 },
    { key: "axle_2_left_outer", cx: 16, cy: 388 },
    { key: "axle_2_left_inner", cx: 34, cy: 388 },
    { key: "axle_2_right_inner", cx: 206, cy: 388 },
    { key: "axle_2_right_outer", cx: 224, cy: 388 },
    // Legacy keys for historical PTIs (only shown when that key is active/has photo)
    { key: "axle_1_left", cx: 25, cy: 358 },
    { key: "axle_1_right", cx: 215, cy: 358 },
    { key: "axle_2_left", cx: 25, cy: 388 },
    { key: "axle_2_right", cx: 215, cy: 388 },
];

const PtiMiniDiagram: React.FC<PtiMiniDiagramProps> = ({
    assetType,
    highlightPositionKey,
    highlightItemType,
    width = 160,
    onZonePress,
    photoZones = [],
}) => {
    const theme = useTheme();
    const outlineStroke = alpha(theme.palette.text.primary, 0.55);
    const zoneStroke = alpha(theme.palette.text.primary, 0.42);
    const inactiveFill = alpha(theme.palette.text.primary, 0.1);
    const highlight = theme.palette.primary.main;
    const success = theme.palette.success.main;
    const interactive = Boolean(onZonePress);

    const isHighlighted = (positionKey: string, itemType: PtiItemType) =>
        highlightItemType === itemType && positionKey === highlightPositionKey;

    const hasPhoto = (positionKey: string, itemType: PtiItemType) =>
        photoZones.some(
            (zone) => zone.positionKey === positionKey && zone.itemType === itemType
        );

    const zoneStyle = (positionKey: string, itemType: PtiItemType) => {
        const active = isHighlighted(positionKey, itemType);
        const done = hasPhoto(positionKey, itemType);

        if (active && done) {
            return {
                fill: alpha(success, 0.35),
                stroke: highlight,
                strokeWidth: 3,
            };
        }

        if (active) {
            return {
                fill: alpha(highlight, 0.45),
                stroke: highlight,
                strokeWidth: 3,
            };
        }

        if (done) {
            return {
                fill: alpha(success, 0.35),
                stroke: success,
                strokeWidth: 2.5,
            };
        }

        return {
            fill: inactiveFill,
            stroke: zoneStroke,
            strokeWidth: 2,
        };
    };

    const zoneProps = (positionKey: string, itemType: PtiItemType) =>
        interactive
            ? {
                  role: "button" as const,
                  tabIndex: 0,
                  style: { cursor: "pointer" } as React.CSSProperties,
                  onClick: () => onZonePress?.(positionKey, itemType),
                  onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onZonePress?.(positionKey, itemType);
                      }
                  },
              }
            : {};

    const renderBodyZone = (zone: { key: string; x: number; y: number; w: number; h: number }) => {
        const style = zoneStyle(zone.key, "BodyPhoto");

        return (
            <rect
                key={zone.key}
                x={zone.x}
                y={zone.y}
                width={zone.w}
                height={zone.h}
                rx={4}
                fill={style.fill}
                stroke={style.stroke}
                strokeWidth={style.strokeWidth}
                {...zoneProps(zone.key, "BodyPhoto")}
            />
        );
    };

    const renderVehicleIconOverlays = (vbW: number, vbH: number) =>
        V_BODY.filter((zone) => isPtiIconBodyZone(zone.key)).map((zone) => {
            const done = hasPhoto(zone.key, "BodyPhoto");
            const style = zoneStyle(zone.key, "BodyPhoto");
            const iconColor = done ? success : style.stroke;
            const leftPct = ((zone.x + zone.w / 2) / vbW) * 100;
            const topPct = ((zone.y + zone.h / 2) / vbH) * 100;
            const iconSize = getPtiZoneIconSize(zone.key);

            return (
                <Box
                    key={`icon-${zone.key}`}
                    sx={{
                        position: "absolute",
                        left: `${leftPct}%`,
                        top: `${topPct}%`,
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 0,
                    }}
                >
                    {done ? (
                        <CheckCircleIcon sx={{ fontSize: iconSize, color: iconColor }} />
                    ) : (
                        <PtiZoneIcon positionKey={zone.key} color={iconColor} />
                    )}
                </Box>
            );
        });

    const renderTireZone = (tire: { key: string; cx: number; cy: number }) => {
        const style = zoneStyle(tire.key, "Tire");
        return (
            <rect
                key={tire.key}
                x={tire.cx - 8}
                y={tire.cy - 13}
                width={16}
                height={26}
                rx={4}
                fill={style.fill}
                stroke={style.stroke}
                strokeWidth={style.strokeWidth}
                {...zoneProps(tire.key, "Tire")}
            />
        );
    };

    if (assetType === "Vehicle") {
        const vbW = 240;
        const vbH = 340;
        const visibleTires = V_TIRES.filter((tire) => {
            const isLegacy =
                tire.key === "drive_1_left" ||
                tire.key === "drive_1_right" ||
                tire.key === "drive_2_left" ||
                tire.key === "drive_2_right";
            if (!isLegacy) {
                return true;
            }
            return isHighlighted(tire.key, "Tire") || hasPhoto(tire.key, "Tire");
        });

        return (
            <Box sx={{ width, aspectRatio: `${vbW} / ${vbH}`, position: "relative" }}>
                <svg width="100%" height="100%" viewBox={`0 0 ${vbW} ${vbH}`}>
                    <path d={V_PATH} fill="none" stroke={outlineStroke} strokeWidth={2.5} />
                    <g data-layer="body">{V_BODY.map(renderBodyZone)}</g>
                    {/* Tires last so they paint and receive clicks above body sides */}
                    <g data-layer="tires">{visibleTires.map(renderTireZone)}</g>
                </svg>
                {renderVehicleIconOverlays(vbW, vbH)}
            </Box>
        );
    }

    const vbW = 240;
    const vbH = 420;
    const visibleTires = T_TIRES.filter((tire) => {
        const isLegacy =
            tire.key === "axle_1_left" ||
            tire.key === "axle_1_right" ||
            tire.key === "axle_2_left" ||
            tire.key === "axle_2_right";
        if (!isLegacy) {
            return true;
        }
        return isHighlighted(tire.key, "Tire") || hasPhoto(tire.key, "Tire");
    });

    return (
        <Box sx={{ width, aspectRatio: `${vbW} / ${vbH}`, position: "relative" }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${vbW} ${vbH}`}>
                <rect
                    x={52}
                    y={4}
                    width={136}
                    height={412}
                    rx={8}
                    fill="none"
                    stroke={outlineStroke}
                    strokeWidth={2.5}
                />
                <circle cx={120} cy={10} r={5} fill={zoneStroke} />
                <g data-layer="body">{T_BODY.map(renderBodyZone)}</g>
                <g data-layer="tires">{visibleTires.map(renderTireZone)}</g>
            </svg>
        </Box>
    );
};

export default PtiMiniDiagram;
